import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { apiData } from "../services/api_service";
import { baseURL } from "../config";
import { formatAED } from "../utils/format";

const URL = `${baseURL}/order/cancelled-received`;

const fmt = (v) => (v ? dayjs(v).format("DD-MM-YYYY") : "-");

// Money received on documents that were later cancelled. Nothing is deleted:
// the cancelled document and its payments stay on record, and are reported
// here, in the list footers and on the dashboard whenever there is a value.
// type: "invoice" | "cdn" | "proforma" | undefined (all three).
export function useCancelledReceived(type, filters, refreshKey) {
  const [data, setData] = useState({ total: 0, count: 0, summary: {} });
  const key = JSON.stringify(filters || {});
  useEffect(() => {
    let off = false;
    apiData("get", URL, { ...(filters || {}), ...(type ? { type } : {}) })
      .then((res) => {
        if (!off && res?.success) setData(res.data || { total: 0, count: 0, summary: {} });
      })
      .catch(() => {});
    return () => {
      off = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, key, refreshKey]);
  return data;
}

export default function CancelledReceivedDialog({ open, onClose, type, filters }) {
  const [state, setState] = useState({ loading: false, data: null });

  useEffect(() => {
    if (!open) return;
    let off = false;
    setState({ loading: true, data: null });
    apiData("get", URL, { ...(filters || {}), ...(type ? { type } : {}) })
      .then((res) => {
        if (!off) setState({ loading: false, data: res?.success ? res.data : null });
      })
      .catch(() => {
        if (!off) setState({ loading: false, data: null });
      });
    return () => {
      off = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, JSON.stringify(filters || {})]);

  const d = state.data;
  const title = { invoice: "Cancelled invoices", cdn: "Cancelled CDNs", proforma: "Cancelled Proformas" }[type] || "Cancelled documents";

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>{title} — payment received</Typography>
          <Typography sx={{ fontSize: 13, color: "#64748B", fontWeight: 400 }}>
            Cancelled documents are kept on record. This is the money that had already been received against them.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {state.loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : !d || d.rows.length === 0 ? (
          <Typography sx={{ py: 3, textAlign: "center", color: "#64748B" }}>Nothing to show.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, color: "#475569", fontSize: 12.5 } }}>
                <TableCell>Type</TableCell>
                <TableCell>Number</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Cancelled on</TableCell>
                <TableCell align="right">Received (AED)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d.rows.map((r) => (
                <TableRow key={`${r.type}-${r.id}`}>
                  <TableCell>{r.type_label}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{r.number}</TableCell>
                  <TableCell>{r.customer || "-"}</TableCell>
                  <TableCell>{fmt(r.doc_date)}</TableCell>
                  <TableCell>{fmt(r.cancelled_on)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "#dc2626" }}>
                    {formatAED(r.received)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ "& td": { fontWeight: 800, borderBottom: "none" } }}>
                <TableCell colSpan={5} align="right">
                  Total ({d.count})
                </TableCell>
                <TableCell align="right">{formatAED(d.total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: "none", borderRadius: "8px" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
